
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentThought, setCurrentThought] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  const thoughts = [
    "What if education could ignite revolutions?",
    "What if technology could bridge every gap?",
    "What if creativity could reshape Africa?",
    "What if innovation started with you?"
  ];

  const impactWords = ["TRANSFORM", "INNOVATE", "ELEVATE", "INSPIRE"];

  const testimonials = [
    {
      text: "TheSpire didn't just teach me to code - they taught me to think like an innovator. Now I'm building solutions that matter.",
      name: "Amina Hassan",
      role: "Full-Stack Developer",
      company: "Leading Tech Company"
    },
    {
      text: "From zero coding knowledge to landing my dream job in 6 months. TheSpire's approach is revolutionary.",
      name: "Chidi Okafor",
      role: "Software Engineer",
      company: "Top Fintech Startup"
    },
    {
      text: "The scholarship program changed my life. Now I'm building apps that serve millions across Africa.",
      name: "Fatima Abdullahi",
      role: "Mobile Developer",
      company: "Global Tech Company"
    }
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentThought((prev) => (prev + 1) % thoughts.length);
    }, 4_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const currentWord = impactWords[currentWordIndex];
    let charIndex = 0;

    const typeInterval = setInterval(() => {
      if (charIndex <= currentWord.length) {
        setTypedText(currentWord.slice(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setTimeout(() => {
          setCurrentWordIndex((prev) => (prev + 1) % impactWords.length);
          setTypedText('');
        }, 2_000);
      }
    }, 150);

    return () => clearInterval(typeInterval);
  }, [currentWordIndex, isVisible]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "TheSpire",
    url: `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`,
    logo: "https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/96972e403c7caa0756821f23026fdd33.svg",
    description: "Transforming Africa through technology, creativity, and education. We don't just teach - we ignite revolutions.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Wuse 2",
      addressLocality: "Abuja",
      addressRegion: "Federal Capital Territory",
      addressCountry: "Nigeria"
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+234-916-771-0173",
      contactType: "customer service",
      email: "info@thespire.com.ng"
    },
    sameAs: [
      "https://wa.me/2349167710173"
    ],
    foundingDate: "2020",
    founder: [
      {
        "@type": "Person",
        "name": "Abdullahi Saidi"
      },
      {
        "@type": "Person",
        "name": "Aysha Idris"
      }
    ],
    areaServed: {
      "@type": "Country",
      "name": "Nigeria"
    },
    serviceArea: {
      "@type": "Country",
      "name": "Nigeria"
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <SEO
        title="TheSpire - Where Innovation Meets Impact"
        description="Transforming Africa through technology, creativity, and education. We don't just teach - we ignite revolutions."
        keywords="Spire, The Spire, TheSpire, Thespire, tech training, software development, creative courses, Africa, Nigeria, innovation, technology education, digital transformation"
        structuredData={structuredData}
      />

      {/* Revolutionary Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 pt-24 pb-12 sm:pt-28 sm:pb-16"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, #6D03F4 0%, #0A0A0A 70%)`
        }}
      >
        {/* Animated Grid Background */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(109, 3, 244, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(109, 3, 244, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '30px 30px',
            transform: `translate(${scrollY * 0.5}px, ${scrollY * 0.3}px)`
          }}
        />

        {/* Floating Particles - Reduced on mobile */}
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 sm:w-2 sm:h-2 bg-[#6D03F4] rounded-full opacity-60 hidden sm:block"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 2}s`,
              transform: `translate(${Math.sin(scrollY * 0.01 + i) * 20}px, ${Math.cos(scrollY * 0.01 + i) * 20}px)`
            }}
          />
        ))}

        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            
            {/* Left Side - Main Content */}
            <div className="text-center lg:text-left space-y-6 sm:space-y-8">
              {/* Attention Grabber */}
              <div>
                <div className="inline-flex items-center bg-gradient-to-r from-[#6D03F4]/20 to-[#007FFF]/20 backdrop-blur-sm border border-[#6D03F4]/30 rounded-full px-3 py-1.5 sm:px-4 sm:py-2 mb-3 sm:mb-4">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse mr-2 sm:mr-3"></div>
                  <span className="text-xs sm:text-sm font-semibold text-white">2,500+ Lives Transformed</span>
                </div>
                <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-light text-gray-300 px-4 sm:px-0 leading-relaxed">
                  {thoughts[currentThought]}
                </h2>
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight">
                FROM ZERO TO
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#6D03F4] to-[#007FFF] mt-1 sm:mt-2">
                  TECH HERO
                </span>
                <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-normal text-gray-300 mt-2 sm:mt-3">
                  in 6 months
                </span>
              </h1>

              {/* Value Proposition */}
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed px-2 sm:px-0">
                Join Africa's most successful tech transformation program. 
                <span className="block sm:inline text-[#6D03F4] font-semibold mt-1 sm:mt-0"> 94% job placement rate.</span>
                <span className="block sm:inline text-[#007FFF] font-semibold mt-1 sm:mt-0"> ₦2.5M average starting salary.</span>
              </p>

              {/* Social Proof */}
              <div>
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4">
                  <div className="flex -space-x-2">
                    {[1,2,3,4,5].map((i) => (
                      <div key={i} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-[#6D03F4] to-[#007FFF] border-2 border-white flex items-center justify-center">
                        <span className="text-white text-xs font-bold">A{i}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex text-yellow-400 text-base sm:text-lg justify-center lg:justify-start">
                      {'★'.repeat(5)}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 text-center lg:text-left">4.9/5 from 500+ graduates</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-md mx-auto lg:mx-0">
                <Link to="/courses" className="w-full sm:flex-1">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-[#6D03F4] to-[#007FFF] hover:from-[#007FFF] hover:to-[#6D03F4] text-white px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base lg:text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap w-full relative overflow-hidden group"
                  >
                    <span className="relative z-10">START YOUR JOURNEY</span>
                    <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  </Button>
                </Link>
                <Link to="/about" className="w-full sm:flex-1">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base lg:text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap w-full"
                  >
                    WATCH SUCCESS STORIES
                  </Button>
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 text-xs sm:text-sm text-gray-400">
                <div className="flex items-center">
                  <i className="ri-shield-check-line text-green-400 mr-1.5 sm:mr-2 text-sm sm:text-base"></i>
                  <span>Money-back guarantee</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-time-line text-blue-400 mr-1.5 sm:mr-2 text-sm sm:text-base"></i>
                  <span>Lifetime access</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-award-line text-yellow-400 mr-1.5 sm:mr-2 text-sm sm:text-base"></i>
                  <span>Industry certified</span>
                </div>
              </div>
            </div>

            {/* Right Side - Visual Impact */}
            <div className="relative mt-8 lg:mt-0">
              {/* Main Hero Image */}
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/9de14be6c635a2d6201c341ac90defcc.jpeg"
                  alt="Successful African Tech Professional"
                  className="w-full h-80 sm:h-96 md:h-[450px] lg:h-[500px] xl:h-[600px] object-cover rounded-xl sm:rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-xl sm:rounded-2xl"></div>
                
                {/* Floating Success Metrics */}
                <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/20">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-[#6D03F4]">₦2.5M</div>
                  <div className="text-[10px] sm:text-xs text-white whitespace-nowrap">Avg. Starting Salary</div>
                </div>

                <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/20">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-green-400">94%</div>
                  <div className="text-[10px] sm:text-xs text-white">Job Placement</div>
                </div>

                <div className="absolute top-1/2 -left-2 sm:-left-4 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/20 transform -translate-y-1/2">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-[#007FFF]">6</div>
                  <div className="text-[10px] sm:text-xs text-white">Months</div>
                </div>
              </div>

              {/* Testimonial Carousel */}
              <div className="mt-4 sm:mt-6 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/20">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-white text-base sm:text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs sm:text-sm mb-2 sm:mb-3 leading-relaxed">
                      "{testimonials[currentTestimonial].text}"
                    </p>
                    <div>
                      <div className="font-semibold text-[#6D03F4] text-xs sm:text-sm truncate">{testimonials[currentTestimonial].name}</div>
                      <div className="text-[10px] sm:text-xs text-gray-400 truncate">{testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}</div>
                    </div>
                  </div>
                </div>
                
                {/* Testimonial Indicators */}
                <div className="flex justify-center mt-3 sm:mt-4 space-x-1.5 sm:space-x-2">
                  {testimonials.map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                        index === currentTestimonial ? 'bg-[#6D03F4] w-5 sm:w-6' : 'bg-white/30 w-1.5 sm:w-2'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Company Logos */}
              <div className="mt-4 sm:mt-6 text-center">
                <p className="text-[10px] sm:text-xs text-gray-400 mb-2 sm:mb-3">Our graduates work at:</p>
                <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 lg:gap-6 opacity-60">
                  <div className="text-white font-bold text-xs sm:text-sm">Top Innovative Companies</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-2 sm:h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="py-16 sm:py-32 bg-white text-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#6D03F4]/5 to-[#007FFF]/5"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-5xl md:text-6xl font-black mb-6 sm:mb-8 leading-tight">
                THIS IS OUR
                <span className="block text-[#6D03F4]">MANIFESTO</span>
              </h2>
              
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg leading-relaxed">
                <p className="font-semibold">
                  We believe that every line of code written, every design crafted, 
                  every solution built has the power to change lives.
                </p>
                
                <p>
                  In a world where technology divides, we unite. Where others see obstacles, 
                  we see opportunities. Where tradition says "impossible," we say "inevitable."
                </p>
                
                <p>
                  We are not just educators. We are revolution architects. 
                  We don't just teach skills—we ignite movements.
                </p>
              </div>
            </div>

            <div className="relative order-1 lg:order-2">
              <img 
                src="https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/489adb74391ae9132915b14e5336d33a.jpeg"
                alt="African Tech Innovators"
                className="w-full h-64 sm:h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -inset-4 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-2xl blur-xl opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-16 sm:py-32 bg-[#0A0A0A] relative">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4]/10 to-[#007FFF]/10"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-black text-white mb-4 sm:mb-6">
              IMPACT IN
              <span className="block text-[#6D03F4]">NUMBERS</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300">Every number tells a story of transformation</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {[
              { number: "2,500+", label: "MINDS TRANSFORMED", icon: "ri-brain-line" },
              { number: "150+", label: "DREAMS FUNDED", icon: "ri-award-line" },
              { number: "300+", label: "SOLUTIONS BUILT", icon: "ri-rocket-line" },
              { number: "50+", label: "PARTNERSHIPS FORGED", icon: "ri-handshake-line" }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${stat.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <div className="text-2xl sm:text-4xl md:text-5xl font-black text-[#6D03F4] mb-2">{stat.number}</div>
                <div className="text-xs sm:text-sm md:text-base text-gray-300 font-bold tracking-wider px-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Showcase */}
      <section className="py-16 sm:py-32 bg-white text-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-black mb-4 sm:mb-6">
              HOW WE
              <span className="block text-[#6D03F4]">REVOLUTIONIZE</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                title: "TECH TRAINING",
                description: "Immersive programs that transform beginners into industry leaders",
                icon: "ri-code-s-slash-line",
                image: "https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/bba053a53883b09b271cce8711b36dd6.jpeg"
              },
              {
                title: "SOFTWARE DEVELOPMENT",
                description: "Custom solutions that power businesses and transform industries",
                icon: "ri-computer-line",
                image: "https://readdy.ai/api/search-image?query=African%20software%20developers%20building%20applications%20on%20multiple%20monitors%2C%20modern%20office%20environment%20with%20sleek%20workstations%2C%20team%20collaboration%20on%20innovative%20software%20projects%2C%20purple%20and%20blue%20tech%20lighting%2C%20professional%20development%20atmosphere&width=400&height=300&seq=software&orientation=landscape"
              },
              {
                title: "DEVICE-AS-A-SERVICE",
                description: "Democratizing access to technology in educational institutions",
                icon: "ri-tablet-line",
                image: "https://readdy.ai/api/search-image?query=Modern%20African%20classroom%20with%20students%20using%20tablets%20and%20digital%20devices%2C%20teacher%20demonstrating%20educational%20technology%2C%20interactive%20learning%20environment%2C%20students%20engaged%20with%20digital%20tools%2C%20contemporary%20educational%20setting%20with%20tech%20integration&width=400&height=300&seq=daas&orientation=landscape"
              },
              {
                title: "COMMUNITY IMPACT",
                description: "Scholarships and partnerships that create lasting change",
                icon: "ri-community-line",
                image: "https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/fa83b659ae76fcac40e7f8451ce2c789.jpeg"
              }
            ].map((service, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-2xl mb-4 sm:mb-6">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-32 sm:h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  
                  {/* Duration Badge */}
                  <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4">
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-[#6D03F4] rounded-full flex items-center justify-center">
                      <i className={`${service.icon} text-sm sm:text-xl text-white`}></i>
                    </div>
                  </div>
                </div>
                
                <h3 className="text-base sm:text-xl font-black mb-2 sm:mb-3 group-hover:text-[#6D03F4] transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 sm:py-32 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white text-center relative overflow-hidden">
        <div className="absolute inset-0">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 sm:w-4 sm:h-4 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${4 + Math.random() * 3}s ease-in-out infinite ${Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-black mb-6 sm:mb-8 leading-tight">
            READY TO CHANGE
            <span className="block">THE WORLD?</span>
          </h2>
          
          <p className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-12 leading-relaxed opacity-90">
            The future doesn't wait. Your revolution starts now.
            Join thousands who've already begun their transformation.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link to="/courses" className="w-full sm:w-auto">
              <Button 
                size="lg"
                className="bg-white text-[#6D03F4] hover:bg-gray-100 px-8 sm:px-12 py-3 sm:py-4 text-base sm:text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap w-full"
              >
                BEGIN YOUR JOURNEY
              </Button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-[#6D03F4] px-8 sm:px-12 py-3 sm:py-4 text-base sm:text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap w-full"
              >
                PARTNER WITH US
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <a
          href="https://wa.me/2349167710173"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 sm:w-14 sm:h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors group"
        >
          <i className="ri-whatsapp-line text-white text-xl sm:text-2xl group-hover:scale-110 transition-transform"></i>
        </a>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes orbit {
          from { transform: translate(-50%, -50%) rotate(0deg) translateY(-80px) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg) translateY(-80px) rotate(-360deg); }
        }
        
        @media (min-width: 640px) {
          @keyframes orbit {
            from { transform: translate(-50%, -50%) rotate(0deg) translateY(-120px) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg) translateY(-120px) rotate(-360deg); }
          }
        }
      `}</style>
    </div>
  );
}
